import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Send, ThumbsUp, HelpCircle, ArrowLeft, Smartphone, Star, ListMusic, Play } from 'lucide-react';
import { Annotation } from './illustrations/Annotations';
import {
  MockHeader, MockTabBar, MockSearchBar, MockSearchResults,
  MockSubmitForm, MockNickname, MockQueueCard, MockQueueList,
  MockRankingsList, MockStatusBadge, MockMyRequests,
  MockOverflowPool, MockOverflowPromoted,
} from './illustrations/MockUI';

// =============================================================================
// Types
// =============================================================================

export type HelpTopicId = 'navigation' | 'requesting' | 'voting' | 'rankings' | 'request-statuses' | 'overflow';

interface HelpStep {
  illustration: React.ReactNode;
  caption: string;
}

interface HelpTopic {
  id: HelpTopicId;
  title: string;
  description: string;
  icon: React.ElementType;
  steps: HelpStep[];
}

interface AttendeeTutorialProps {
  mode: 'consent' | 'help' | 'tour';
  initialHelpTopic?: HelpTopicId;
  isRankedMode: boolean;
  showOverflowHelp: boolean;
  downvotingEnabled: boolean;
  eventName: string;
  onClose: () => void;
  onPermanentDismiss: () => void;
}

type InternalView = 'consent' | 'slideshow' | 'help-index' | 'help-topic';

// =============================================================================
// Caption renderer — parses **bold** into <strong> tags
// =============================================================================

function renderCaption(text: string): React.ReactNode {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}

// =============================================================================
// Help topic content
// =============================================================================

function getHelpTopics(isRankedMode: boolean, showOverflowHelp: boolean, downvotingEnabled: boolean): HelpTopic[] {
  const topics: HelpTopic[] = [];

  // Navigation Overview
  const navStep2Caption = isRankedMode
    ? 'Use the tabs to switch between features. **Request** lets you search for and submit songs. **My Rankings** lets you rank songs to influence the queue.'
    : 'Use the tabs to switch between features. **Request** lets you search for and submit songs. **Vote** shows the queue and lets you upvote or downvote.';

  topics.push({
    id: 'navigation',
    title: 'Finding Your Way Around',
    description: 'The header and tabs explained',
    icon: Smartphone,
    steps: [
      {
        illustration: <MockHeader annotations={[
          { type: 'circle', x: '12%', y: '50%', label: '1' } as Annotation,
          { type: 'circle', x: '72%', y: '50%', label: '2' } as Annotation,
          { type: 'circle', x: '82%', y: '50%', label: '3' } as Annotation,
          { type: 'circle', x: '92%', y: '50%', label: '4' } as Annotation,
        ]} />,
        caption: "The header has quick actions. **QR** shares the event link. **Refresh** reloads the queue. **?** opens this help guide. **Home Screen** saves a shortcut to your phone — no account or app install needed. Note: the shortcut is for this event only and won't work for a different one.",
      },
      {
        illustration: <MockTabBar
          highlightTabs={isRankedMode ? ['request', 'rankings'] : ['request', 'vote']}
          isRankedMode={isRankedMode}
          annotations={[
            { type: 'circle', x: '10%', y: '15%', label: '1' } as Annotation,
            { type: 'circle', x: '27%', y: '15%', label: '2' } as Annotation,
          ]}
        />,
        caption: navStep2Caption,
      },
      {
        illustration: <MockTabBar
          highlightTabs={['my-requests', 'messages']}
          isRankedMode={isRankedMode}
          annotations={[
            { type: 'circle', x: '43%', y: '15%', label: '3' } as Annotation,
            { type: 'circle', x: '60%', y: '15%', label: '4' } as Annotation,
          ]}
        />,
        caption: '**My Requests** shows your submissions and their current status. **Messages** shows announcements from the DJ.',
      },
      {
        illustration: <MockTabBar
          highlightTabs={['stats', 'feedback']}
          isRankedMode={isRankedMode}
          annotations={[
            { type: 'circle', x: '77%', y: '15%', label: '5' } as Annotation,
            { type: 'circle', x: '93%', y: '15%', label: '6' } as Annotation,
          ]}
        />,
        caption: '**Stats** shows live event activity. **Feedback** lets you rate the event when it wraps up.',
      },
    ],
  });

  // Requesting a Song
  topics.push({
    id: 'requesting',
    title: 'Requesting a Song',
    description: 'How to find and submit song requests',
    icon: Send,
    steps: [
      {
        illustration: <MockSearchBar annotations={[
          { type: 'highlight', x: '4%', y: '8%', width: '92%', height: '80%' } as Annotation,
        ]} />,
        caption: 'Tap the search bar and type a song name or artist.',
      },
      {
        illustration: <MockSearchResults annotations={[
          { type: 'arrow', x: '95%', y: '25%', direction: 'left' } as Annotation,
        ]} />,
        caption: 'Browse the results and tap the song you want to request.',
      },
      {
        illustration: <MockSubmitForm annotations={[
          { type: 'circle', x: '92%', y: '48%', label: '1' } as Annotation,
          { type: 'arrow', x: '50%', y: '97%', direction: 'up' } as Annotation,
        ]} />,
        caption: 'Review the details. Add an optional message or dedication, then tap **Submit**.',
      },
      {
        illustration: <MockNickname annotations={[
          { type: 'highlight', x: '4%', y: '10%', width: '92%', height: '75%' } as Annotation,
        ]} />,
        caption: "Your request is submitted under a nickname. You can change it any time by tapping **Change** — or stay anonymous.",
      },
      {
        illustration: <MockStatusBadge status="pending" annotations={[
          { type: 'highlight', x: '68%', y: '20%', width: '28%', height: '55%' } as Annotation,
        ]} />,
        caption: 'Your request appears in **My Requests** with a **Pending** status while the DJ reviews it.',
      },
      {
        illustration: <MockStatusBadge status="queued" annotations={[
          { type: 'highlight', x: '68%', y: '20%', width: '28%', height: '55%' } as Annotation,
        ]} />,
        caption: "Once approved, the status changes to **Queued** — it's in the play line!",
      },
    ],
  });

  // Voting & the Queue (standard mode only)
  if (!isRankedMode) {
    const votingSteps: HelpStep[] = [
      {
        illustration: <MockTabBar highlightTabs={['vote']} activeTab="vote" annotations={[
          { type: 'highlight', x: '16%', y: '5%', width: '17%', height: '90%' } as Annotation,
        ]} />,
        caption: 'Open the **Vote** tab to see all queued songs.',
      },
      {
        illustration: <MockQueueCard showDownvote={false} isUpvoted={true} voteCount={4} annotations={[
          { type: 'arrow', x: '75%', y: '30%', direction: 'left' } as Annotation,
        ]} />,
        caption: 'Tap the **upvote** button on any song to push it higher in the queue. You get one vote per song.',
      },
    ];

    if (downvotingEnabled) {
      votingSteps.push({
        illustration: <MockQueueCard showDownvote={true} annotations={[
          { type: 'circle', x: '75%', y: '35%', label: '1' } as Annotation,
          { type: 'circle', x: '90%', y: '35%', label: '2' } as Annotation,
        ]} />,
        caption: 'You can also **downvote** songs. If a song collects enough downvotes, it may be removed from the queue entirely.',
      });
    }

    votingSteps.push({
      illustration: <MockQueueList annotations={[
        { type: 'highlight', x: '72%', y: '5%', width: '22%', height: '90%' } as Annotation,
      ]} />,
      caption: 'Songs with more votes rise toward the top — your favorites play sooner.',
    });

    topics.push({
      id: 'voting',
      title: 'Voting & the Queue',
      description: 'How votes affect what plays next',
      icon: ThumbsUp,
      steps: votingSteps,
    });
  }

  // My Rankings (ranked mode only)
  if (isRankedMode) {
    const rankingsSteps: HelpStep[] = [
      {
        illustration: <MockTabBar highlightTabs={['rankings']} activeTab="rankings" isRankedMode={true} annotations={[
          { type: 'highlight', x: '16%', y: '5%', width: '17%', height: '90%' } as Annotation,
        ]} />,
        caption: 'Open the **My Rankings** tab to see new songs and your current ranking.',
      },
      {
        illustration: <MockRankingsList annotations={[
          { type: 'arrow', x: '95%', y: '82%', direction: 'left' } as Annotation,
        ]} />,
        caption: 'When new songs appear, tap **+Rank** to add them to your ranking, **unRank** to set them aside, or tap the **NEW** tag to put them at any number.',
      },
      {
        illustration: <MockRankingsList annotations={[
          { type: 'arrow', x: '10%', y: '20%', direction: 'left' } as Annotation,
          { type: 'arrow', x: '10%', y: '35%', direction: 'left' } as Annotation,
        ]} />,
        caption: 'Drag the **⠿** handle to reorder, or tap a song’s **number** to move it straight to another spot. Your top-ranked songs count toward the queue, and your #1 has the most weight.',
      },
      {
        illustration: <MockRankingsList annotations={[
          { type: 'highlight', x: '2%', y: '76%', width: '96%', height: '22%' } as Annotation,
        ]} />,
        caption: 'Your ranking saves automatically. When one of your ranked songs plays, your next song moves up and counts in its place, even with your phone closed.',
      },
    ];

    const step5Caption = downvotingEnabled
      ? 'You can also upvote and downvote songs in the **Vote** tab. Votes and rankings work together to influence the final order. Songs with enough downvotes may be removed from the queue.'
      : 'You can also upvote songs in the **Vote** tab. Votes and rankings work together to influence the final order.';

    rankingsSteps.push({
      illustration: <MockQueueCard showDownvote={downvotingEnabled} annotations={[
        { type: 'arrow', x: '75%', y: '30%', direction: 'left' } as Annotation,
      ]} />,
      caption: step5Caption,
    });

    topics.push({
      id: 'rankings',
      title: 'My Rankings',
      description: 'How to rank songs and influence the queue',
      icon: Star,
      steps: rankingsSteps,
    });
  }

  // Request Statuses
  topics.push({
    id: 'request-statuses',
    title: 'My Request Statuses',
    description: 'What each status means for your requests',
    icon: HelpCircle,
    steps: [
      {
        illustration: <MockMyRequests />,
        caption: 'Your requests move through stages as the DJ manages the queue.',
      },
      {
        illustration: (
          <div className="flex flex-col gap-2">
            <MockStatusBadge status="pending" />
            <MockStatusBadge status="queued" />
          </div>
        ),
        caption: '**Pending** means the DJ is reviewing it. **Queued** means it\'s accepted and in line to play.',
      },
      {
        illustration: (
          <div className="flex flex-col gap-2">
            <MockStatusBadge status="nowPlaying" />
            <MockStatusBadge status="played" />
            <MockStatusBadge status="declined" />
          </div>
        ),
        caption: "**Now Playing** — it's on! **Played** — already went. **Declined** — the DJ passed on this one.",
      },
    ],
  });

  // Overflow Pool
  if (showOverflowHelp) {
    topics.push({
      id: 'overflow',
      title: 'Overflow Pool',
      description: 'What happens when you hit your request limit',
      icon: ListMusic,
      steps: [
        {
          illustration: <MockStatusBadge status="overflow" annotations={[
            { type: 'highlight', x: '68%', y: '20%', width: '28%', height: '55%' } as Annotation,
          ]} />,
          caption: "Hit your request limit? You can still submit songs — they go into your personal **overflow pool** instead of the main queue.",
        },
        {
          illustration: <MockOverflowPool annotations={[
            { type: 'circle', x: '88%', y: '25%', label: '1' } as Annotation,
            { type: 'circle', x: '13%', y: '25%', label: '2' } as Annotation,
          ]} />,
          caption: 'Your overflow songs appear in **My Requests** under the overflow section. Drag the **⠿** handle to set your priority order — the DJ sees your top pick first.',
        },
        {
          illustration: <MockOverflowPromoted annotations={[
            { type: 'highlight', x: '68%', y: '20%', width: '28%', height: '55%' } as Annotation,
          ]} />,
          caption: 'When the DJ raises the request limit, your highest-priority overflow songs are automatically promoted to the queue!',
        },
      ],
    });
  }

  return topics;
}

// =============================================================================
// Getting Started tour sequence
// =============================================================================

function getGettingStartedSteps(isRankedMode: boolean, downvotingEnabled: boolean): HelpStep[] {
  const topics = getHelpTopics(isRankedMode, false, downvotingEnabled);

  const nav = topics.find(t => t.id === 'navigation')!;
  const requesting = topics.find(t => t.id === 'requesting')!;

  const steps: HelpStep[] = [
    nav.steps[0],
    nav.steps[1],
    nav.steps[2],
    requesting.steps[0],
    requesting.steps[1],
    requesting.steps[2],
  ];

  if (isRankedMode) {
    const rankings = topics.find(t => t.id === 'rankings')!;
    steps.push(rankings.steps[1]);
    steps.push(rankings.steps[2]);
  } else {
    const voting = topics.find(t => t.id === 'voting')!;
    steps.push(voting.steps[1]);
  }

  steps.push({
    illustration: <MockHeader annotations={[
      { type: 'highlight', x: '76%', y: '20%', width: '12%', height: '60%' } as Annotation,
    ]} />,
    caption: "You're all set! Tap the **?** button any time to explore detailed help on voting, rankings, statuses, and more.",
  });

  return steps;
}

// =============================================================================
// Step viewer (shared between slideshow and help-topic)
// =============================================================================

function StepViewer({
  steps,
  title,
  stepIndex,
  onStepChange,
  onBack,
  onSkip,
  onDone,
  onClose,
}: {
  steps: HelpStep[];
  title?: string;
  stepIndex: number;
  onStepChange: (index: number) => void;
  onBack?: () => void;
  onSkip?: () => void;
  onDone: () => void;
  onClose: () => void;
}) {
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  return (
    <>
      <div className="flex items-center justify-between p-4 pb-2">
        <div className="flex items-center gap-2">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label="Back to help index"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          {title && <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {stepIndex + 1} / {steps.length}
          </span>
          {onSkip && !isLast && (
            <button
              onClick={onSkip}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              Skip
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close help"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="px-4 pb-2 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 120px)' }}>
        <div className="flex justify-center mb-3">
          {step.illustration}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {renderCaption(step.caption)}
        </p>
      </div>

      <div className="flex items-center justify-center gap-2 px-4 py-2">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`rounded-full transition-all duration-200 ${
              i === stepIndex
                ? 'w-4 h-2 bg-primary'
                : 'w-2 h-2 bg-gray-300 dark:bg-gray-600'
            }`}
          />
        ))}
      </div>

      <div className="flex items-center justify-between px-4 pb-4">
        <button
          onClick={() => onStepChange(Math.max(0, stepIndex - 1))}
          disabled={stepIndex === 0}
          className="p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {isLast ? (
          <button onClick={onDone} className="btn btn-primary px-8">
            Done
          </button>
        ) : (
          <button
            onClick={() => onStepChange(Math.min(steps.length - 1, stepIndex + 1))}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );
}

// =============================================================================
// Component
// =============================================================================

export default function AttendeeTutorial({
  mode,
  initialHelpTopic,
  isRankedMode,
  showOverflowHelp,
  downvotingEnabled,
  eventName,
  onClose,
  onPermanentDismiss,
}: AttendeeTutorialProps) {
  const [view, setView] = useState<InternalView>(() => {
    if (mode === 'help') return 'help-index';
    if (mode === 'tour') return 'slideshow';
    return 'consent';
  });
  const [stepIndex, setStepIndex] = useState(0);
  const [activeTopic, setActiveTopic] = useState<HelpTopicId | null>(initialHelpTopic ?? null);
  const [noThanksExpanded, setNoThanksExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (mode === 'help' && initialHelpTopic) {
      setActiveTopic(initialHelpTopic);
      setView('help-topic');
    }
  }, [mode, initialHelpTopic]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const helpTopics = getHelpTopics(isRankedMode, showOverflowHelp, downvotingEnabled);
  const gettingStartedSteps = getGettingStartedSteps(isRankedMode, downvotingEnabled);

  const cardBase = `
    relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md mx-4
    transition-all duration-200 max-h-[85vh] flex flex-col
    ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
  `;

  // ─── Consent view ─────────────────────────────────────────────────────────

  if (view === 'consent') {
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className={`${cardBase} p-6 sm:p-8 mb-4 sm:mb-0`}>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-1 truncate">{eventName}</p>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Want a quick tour?</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            We'll show you how to request songs and influence what plays. Takes about 30 seconds.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => { setStepIndex(0); setView('slideshow'); }}
              className="btn btn-primary w-full"
            >
              Yes, show me
            </button>

            {!noThanksExpanded ? (
              <button
                onClick={() => setNoThanksExpanded(true)}
                className="btn btn-outline w-full"
              >
                No thanks
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  onClick={onPermanentDismiss}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors py-1"
                >
                  Don't show again
                </button>
                <button
                  onClick={onClose}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors py-1"
                >
                  Maybe later
                </button>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
            You can always find help using the ? button at the top of the page.
          </p>
        </div>
      </div>
    );
  }

  // ─── Slideshow view (Getting Started tour) ────────────────────────────────

  if (view === 'slideshow') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
        <div className={`${cardBase} mb-4 sm:mb-0`}>
          <StepViewer
            steps={gettingStartedSteps}
            stepIndex={stepIndex}
            onStepChange={setStepIndex}
            onSkip={onPermanentDismiss}
            onDone={onPermanentDismiss}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  // ─── Help index view ──────────────────────────────────────────────────────

  if (view === 'help-index') {
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className={`${cardBase} mb-4 sm:mb-0`}>
          <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Help</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label="Close help"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 flex flex-col gap-2 max-h-96 overflow-y-auto">
            <button
              onClick={() => { setStepIndex(0); setView('slideshow'); }}
              className="flex items-center gap-3 p-3 rounded-xl text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Play className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Getting Started</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Replay the quick tour</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
            </button>

            {helpTopics.map((topic) => {
              const TopicIcon = topic.icon;
              return (
                <button
                  key={topic.id}
                  onClick={() => { setActiveTopic(topic.id); setStepIndex(0); setView('help-topic'); }}
                  className="flex items-center gap-3 p-3 rounded-xl text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <TopicIcon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{topic.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{topic.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── Help topic view ──────────────────────────────────────────────────────

  const topic = helpTopics.find((t) => t.id === activeTopic);

  if (!topic) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`${cardBase} mb-4 sm:mb-0`}>
        <StepViewer
          steps={topic.steps}
          title={topic.title}
          stepIndex={stepIndex}
          onStepChange={setStepIndex}
          onBack={() => { setView('help-index'); setStepIndex(0); }}
          onDone={() => { setView('help-index'); setStepIndex(0); }}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
